/**
 * The Ember reference skeleton: proportions, the pose a clip asks for, and the solver that turns
 * that pose into joint positions.
 *
 * Model space: y up, +z the way the body faces, +x the character's LEFT. The ground is y = 0 and
 * the pelvis stands over the origin. Units are world pixels; the camera (raster.ts) foreshortens
 * height by cos(pitch), so a standing body is drawn about 46px tall, the height of an LPC body.
 *
 * Every pose is solved here and nowhere else, so the shapes that are drawn (body.ts) and the
 * numbers the game reads off a frame (sockets, soles) can never disagree.
 */
import {
    IDENTITY,
    Mat3,
    Vec3,
    add,
    apply,
    cross,
    distance,
    dot,
    mul,
    normalize,
    rotX,
    rotY,
    rotZ,
    scale,
    sub,
} from "./vec"

export interface Proportions {
    ankleHeight: number
    shin: number
    thigh: number
    /** Half the distance between the hip joints. */
    hipWidth: number
    /** Hip joints sit this far below the pelvis centre. */
    hipDrop: number
    /** Pelvis centre to chest centre. */
    spine: number
    /** Chest centre to the base of the neck. */
    neck: number
    /** Base of the neck to the centre of the head. */
    headCenter: number
    /** Half the distance between the shoulder joints. */
    shoulderWidth: number
    /** Shoulder joints above the chest centre. */
    shoulderHeight: number
    upperArm: number
    forearm: number
    footLength: number
    // Shape: radii of the volumes hung on the bones (body.ts).
    /** Half extents of the skull, a rounded box of squareness `headRoundness`. */
    head: Vec3
    /**
     * How far the skull sits above the head's centre: its base clears the neck at the back while
     * the jaw carries the chin lower at the front, so a side-on head has a jawline and a neck.
     */
    craniumLift: number
    headRoundness: number | Vec3
    /** Ear centres and eye centres below the head centre (negative is down). */
    earHeight: number
    eyeHeight: number
    /** Half the distance between the eyes, in the face. */
    eyeSpacing: number
    /** How far back from the face's front the near eye sits in a side (three-quarter) facing. */
    profileEyeInset: number
    torso: Vec3
    torsoRoundness: number | Vec3
    /**
     * The volume whose top makes the shoulder line: radii, centre above the chest, the cut below
     * which it does not exist, and its squareness (LPC shoulders are broad and flat-topped).
     */
    girdle: { radii: Vec3; drop: number; cut: number; exponent?: number }
    deltoidRadius: number
    pelvis: Vec3
    pelvisRoundness: number | Vec3
    /** How far the chest sits forward of the spine: a little, for an upright chest. */
    posture: number
    neckRadius: number
    upperArmRadius: number
    forearmRadius: number
    handRadius: number
    /** How far the fist hangs in toward the body from the wrist: LPC's hands rest against the thighs. */
    handInset: number
    /** A hand reaching this far past the wrist, open; without it the hand is LPC's round fist. */
    handLength?: number
    thighRadius: number
    shinRadius: number
    footRadius: number
    /**
     * Half the foot's width across, as drawn from the front and back -- where LPC draws feet far
     * broader than a real foot, wider than the shin -- and as drawn side-on, where it is a foot's.
     */
    footWidth: number
    footProfileWidth: number
    /** How far clothes stand off the skin, as a share of what suits the LPC body's thick limbs. */
    clothThickness?: number
    /** A woman's bust: the radius of each breast on the chest's front (upperBody), clothed with it. */
    bust?: number
    /** A heavily muscled chest: each pectoral's radius (upperBody, clothed with it), and abs painted below. */
    pecs?: number
    /**
     * How far below the pelvis point its volume sits: widest at the hip joints rather than at the
     * belly, it tapers up into a waist -- a woman's hips (lowerBody, so garments follow).
     */
    pelvisLow?: number
    /**
     * A waist bridging ribcage and pelvis, its radii, centred `waistHeight` above the pelvis point:
     * the waist curves into the hips instead of the pelvis standing out from it as a ledge.
     */
    waist?: Vec3
    waistHeight?: number
    /**
     * An athlete's legs (body.ts thighOf/shinOf, so garments over the leg follow): `quad` the radius
     * of the muscle swelling the front and outside of each thigh, high, tapering into the knee;
     * `calf` the radius of each head of the calf behind the upper shin, narrowing to a slim ankle.
     */
    quad?: number
    calf?: number
    /** How this body stands still, where it differs from the LPC-fitted STANDING poses. */
    stance?: { [K in keyof typeof STANDING]?: Partial<(typeof STANDING)[K]> }
}

/** Calibrated against the LPC male human: head top at row 15 and soles at row 61 of a 64px frame. */
export const DEFAULT_PROPORTIONS: Proportions = {
    ankleHeight: 2.19,
    shin: 6.5,
    thigh: 6.9,
    hipWidth: 6.0,
    hipDrop: 1.11,
    spine: 7.6,
    neck: 5,
    headCenter: 8.57,
    shoulderWidth: 9.9,
    shoulderHeight: 0.93,
    upperArm: 6.49,
    forearm: 5.19,
    footLength: 4.3,
    head: [10.05, 8.7, 9.15],
    craniumLift: 0.65,
    headRoundness: [2.6, 2.6, 2.3],
    earHeight: -3.71,
    eyeHeight: -2.9,
    eyeSpacing: 4.0,
    profileEyeInset: 4.8,
    torso: [8.6, 6.42, 7.13],
    torsoRoundness: [2.7, 2.6, 2.1],
    girdle: { radii: [12, 4.26, 4.0], drop: 3, cut: 0, exponent: 2.2 },
    deltoidRadius: 3.5,
    pelvis: [7.8, 5.4, 7.3],
    pelvisRoundness: [2.8, 2.8, 2.1],
    posture: 0.5,
    neckRadius: 2.8,
    upperArmRadius: 3.9,
    forearmRadius: 3,
    handRadius: 3.5,
    handInset: 1.2,
    thighRadius: 3.7,
    shinRadius: 3.5,
    footRadius: 2.8,
    footWidth: 4.3,
    footProfileWidth: 3.0,
}

export const legLength = (p: Proportions): number => p.thigh + p.shin

/** Shoulder: forward raise (pitch), outward raise (abduct), elbow bend toward the front (flex). */
export interface ArmPose {
    pitch: number
    abduct: number
    flex: number
    /**
     * The wrist's cock: how far a held tool's shaft turns forward from the forearm's line, a third
     * hinge after shoulder and elbow. Carrying, it stands out ahead of the fist (WRIST_REST);
     * striking, it uncocks toward the forearm's line so the tool follows through.
     */
    wrist?: number
    /**
     * Reach for a point instead: the hand goes to `reach` (from the shoulder, in the chest's frame)
     * with the elbow bent toward `pole`, the angles above unused -- an archer's hands, one at arm's
     * length on the target and one drawn back to the jaw, are places, not angles. `grip` (chest
     * frame) then says which way a held thing points.
     */
    reach?: Vec3
    pole?: Vec3
    grip?: Vec3
}

/** Carrying: the shaft up and ahead of the fist, and tipped out from the body so it shows from the front. */
export const WRIST_REST = (125 * Math.PI) / 180
const GRIP_OUT = (22 * Math.PI) / 180

/** A leg placed by where its ankle goes, in the ground frame. Planted feet use this. */
export interface LegTarget {
    mode: "ik"
    ankle: Vec3
    /** Toes down is positive. */
    footPitch: number
    /** Bearing weight on the ground: the foot must not slide or lift (footsteps, dust, tests). */
    planted?: boolean
}

/** A leg posed by its angles, relative to the pelvis. Falls and kicks use this. */
export interface LegAngles {
    mode: "fk"
    pitch: number
    abduct: number
    /** Knee bend toward the back. */
    flex: number
    footPitch: number
}

export type LegPose = LegTarget | LegAngles

/** What a clip asks for at one instant. Angles in radians. */
export interface PoseSample {
    /** Pelvis centre in the ground frame. */
    root: Vec3
    /** Whole-body orientation about the pelvis (a fall rolls this). */
    rootRotation: Mat3
    /** Upper body: forward lean, twist (+ turns the chest toward +x), side bend. */
    lean: number
    twist: number
    sideBend: number
    headPitch: number
    headYaw: number
    arms: { L: ArmPose; R: ArmPose }
    legs: { L: LegPose; R: LegPose }
    /** How far a bow is drawn: 0 at rest, 1 at full draw; an arrow is nocked while it is above 0. */
    draw?: number
}

export type Side = "L" | "R"
export const SIDES: Side[] = ["L", "R"]
/** +1 for the left side (+x), -1 for the right. */
export const sideSign = (side: Side): number => (side === "L" ? 1 : -1)

export interface LimbJoints {
    shoulder: Vec3
    elbow: Vec3
    hand: Vec3
    hip: Vec3
    knee: Vec3
    ankle: Vec3
    heel: Vec3
    toe: Vec3
    /** Which way a tool held in this hand points from the fist (unit). */
    grip: Vec3
    /**
     * Square to `grip`, in the plane the wrist cocks in: the way a held blade's edge faces, so its
     * width lies in the plane it swings through -- seen broad in a cut, not edge-on.
     */
    edge: Vec3
}

/** A solved pose: every joint, plus the frames the ellipsoids are aligned to. */
export interface Skeleton {
    pelvis: Vec3
    chest: Vec3
    neck: Vec3
    head: Vec3
    pelvisBasis: Mat3
    torsoBasis: Mat3
    headBasis: Mat3
    L: LimbJoints
    R: LimbJoints
    /** The pose's bow draw, carried through for what the hands hold (body.ts). */
    draw?: number
}

const DOWN: Vec3 = [0, -1, 0]
/** The pelvis turns against the chest by this share of the twist. */
export const PELVIS_COUNTER_TWIST = 0.6
/** How far the heel reaches behind the ankle. */
export const HEEL = 1.0

/**
 * Two-bone IK: the knee that puts the ankle at `target`, bending toward `pole`. A target out of
 * reach is pulled in along the line from the hip, so the leg straightens rather than tearing.
 */
export const solveTwoBone = (root: Vec3, target: Vec3, a: number, b: number, pole: Vec3): { mid: Vec3; end: Vec3 } => {
    const toTarget = sub(target, root)
    const reach = Math.max(Math.abs(a - b) + 1e-3, Math.min(a + b - 1e-4, Math.sqrt(dot(toTarget, toTarget))))
    const dir = normalize(toTarget)
    const end = add(root, scale(dir, reach))
    // Distance along the root->end line to the foot of the knee's perpendicular.
    const along = (a * a - b * b + reach * reach) / (2 * reach)
    const out = Math.sqrt(Math.max(0, a * a - along * along))
    // The part of the pole perpendicular to the leg's line is the way the knee points.
    let bend = sub(pole, scale(dir, dot(pole, dir)))
    if (dot(bend, bend) < 1e-8) {
        bend = normalize(cross(dir, [1, 0, 0]))
    }
    const mid = add(add(root, scale(dir, along)), scale(normalize(bend), out))
    return { mid, end }
}

export const solvePose = (pose: PoseSample, p: Proportions): Skeleton => {
    const root = pose.rootRotation
    const torsoBasis = mul(root, mul(rotY(pose.twist), mul(rotX(pose.lean), rotZ(pose.sideBend))))
    const pelvisBasis = mul(root, rotY(-pose.twist * PELVIS_COUNTER_TWIST))
    const headBasis = mul(
        root,
        mul(rotY(pose.twist * 0.4 + pose.headYaw), rotX(pose.lean * 0.35 + pose.headPitch)),
    )

    const pelvis = pose.root
    const chest = add(pelvis, apply(torsoBasis, [0, p.spine, 0]))
    const neck = add(chest, apply(torsoBasis, [0, p.neck, 0]))
    const head = add(neck, apply(headBasis, [0, p.headCenter, 0.4]))
    // Knees point the way the pelvis faces.
    const forward = apply(pelvisBasis, [0, 0, 1])

    const limb = (side: Side): LimbJoints => {
        const s = sideSign(side)
        const arm = pose.arms[side]
        const armFrame = mul(torsoBasis, mul(rotZ(s * arm.abduct), rotX(-arm.pitch)))
        const shoulder = add(chest, apply(torsoBasis, [s * p.shoulderWidth, p.shoulderHeight, 0]))
        let elbow: Vec3, hand: Vec3, grip: Vec3, edge: Vec3
        if (arm.reach) {
            const pole = apply(torsoBasis, arm.pole ?? [0, 0, -1])
            const solved = solveTwoBone(shoulder, add(shoulder, apply(torsoBasis, arm.reach)), p.upperArm, p.forearm, pole)
            elbow = solved.mid
            hand = solved.end
            grip = arm.grip ? normalize(apply(torsoBasis, arm.grip)) : normalize(sub(hand, elbow))
            // Away from the elbow's bend, square to the grip.
            const away = sub(scale(pole, -1), scale(grip, dot(pole, grip) * -1))
            edge = dot(away, away) > 1e-8 ? normalize(away) : normalize(cross(grip, [1, 0, 0]))
        } else {
            elbow = add(shoulder, scale(apply(armFrame, DOWN), p.upperArm))
            hand = add(elbow, scale(apply(mul(armFrame, rotX(-arm.flex)), DOWN), p.forearm))
            const gripFrame = mul(armFrame, mul(rotX(-(arm.flex + (arm.wrist ?? WRIST_REST))), rotZ(s * GRIP_OUT)))
            grip = apply(gripFrame, DOWN)
            edge = apply(gripFrame, [0, 0, -1])
        }

        const hip = add(pelvis, apply(pelvisBasis, [s * p.hipWidth, -p.hipDrop, 0]))
        const leg = pose.legs[side]
        let knee: Vec3
        let ankle: Vec3
        if (leg.mode === "ik") {
            const solved = solveTwoBone(hip, leg.ankle, p.thigh, p.shin, forward)
            knee = solved.mid
            ankle = solved.end
        } else {
            const legFrame = mul(pelvisBasis, mul(rotZ(s * leg.abduct), rotX(-leg.pitch)))
            knee = add(hip, scale(apply(legFrame, DOWN), p.thigh))
            ankle = add(knee, scale(apply(mul(legFrame, rotX(leg.flex)), DOWN), p.shin))
        }
        // The foot hangs off the ankle in the pelvis's heading, pitched by the clip.
        const footFrame = mul(pelvisBasis, rotX(leg.footPitch))
        const sole = p.ankleHeight - p.footRadius
        const toe = add(ankle, apply(footFrame, [0, -sole, p.footLength]))
        const heel = add(ankle, apply(footFrame, [0, -sole, -HEEL]))
        return { shoulder, elbow, hand, hip, knee, ankle, heel, toe, grip, edge }
    }

    return { pelvis, chest, neck, head, pelvisBasis, torsoBasis, headBasis, L: limb("L"), R: limb("R"), draw: pose.draw }
}

/**
 * Where a hip joint stands over the ground (x, z) for a pelvis swayed sideways by `sway` and
 * twisted by the upper body's `twist` -- the pelvis counter-turns, as solvePose has it.
 */
export const hipOver = (side: Side, sway: number, twist: number, p: Proportions): [number, number] => {
    const hip = apply(rotY(-twist * PELVIS_COUNTER_TWIST), [sideSign(side) * p.hipWidth, 0, 0])
    return [sway + hip[0], hip[2]]
}

/** The highest the pelvis may stand while a planted ankle at `ankle` is still reachable from `hip`. */
export const maxPelvisHeight = (ankle: Vec3, hip: [number, number], p: Proportions): number => {
    const dx = ankle[0] - hip[0]
    const dz = ankle[2] - hip[1]
    const reach = legLength(p) * 0.995
    const vertical = Math.sqrt(Math.max(0, reach * reach - dx * dx - dz * dz))
    return ankle[1] + vertical + p.hipDrop
}

export const standingPelvisHeight = (p: Proportions): number => p.ankleHeight + legLength(p) + p.hipDrop

/** Mirror a vector across the body's midline (x -> -x). */
export const mirrorX = (v: Vec3): Vec3 => [-v[0], v[1], v[2]]

/**
 * Standing still. Like LPC's, the pose is not one 3D pose seen from four sides. From the front the
 * arms bow out from the body; from the side (where the body stands three-quarters on, see
 * raster.ts SIDE_TURN) they hang in close, or they would poke out of the outline.
 */
/** The standing pose, front-on and side-on: fitted to the LPC body's silhouettes (see restingSample). */
export const STANDING = {
    front: { pitch: 0.22, abduct: 0.25, flex: 0.3, feetOut: 1.3 },
    // From behind, LPC stands its feet a little wider than from the front.
    back: { pitch: 0.22, abduct: 0.25, flex: 0.3, feetOut: 1.8 },
    side: { pitch: -0.2, abduct: 0.11, flex: 0.55, feetOut: 0.4, feetSplit: 0.5 },
}

/** Seen side-on, the side of the body away from the camera: a figure facing left shows its left. */
export const farSide = (facing?: string): Side => (facing?.endsWith("left") ? "R" : "L")

export const restingSample = (p: Proportions, facing?: string): PoseSample => {
    const side = facing === "left" || facing === "right"
    const which = side ? "side" : facing?.startsWith("back") ? "back" : "front"
    const pose = { feetSplit: 0, ...STANDING[which], ...p.stance?.[which] }
    // From the side the far foot leads (the body stands three-quarters on, its far hip forward).
    const far = farSide(facing)
    const stance = (s: Side): LegTarget => ({
        mode: "ik",
        ankle: [
            sideSign(s) * (p.hipWidth + pose.feetOut),
            p.ankleHeight,
            side ? (s === far ? 1 : -1) * pose.feetSplit : 0,
        ],
        footPitch: 0,
    })
    const arm = { pitch: pose.pitch, abduct: pose.abduct, flex: pose.flex }
    return {
        root: [0, standingPelvisHeight(p) - 0.4, 0],
        rootRotation: IDENTITY,
        lean: 0,
        twist: 0,
        sideBend: 0,
        headPitch: 0,
        headYaw: 0,
        arms: { L: arm, R: arm },
        legs: { L: stance("L"), R: stance("R") },
    }
}

export const jointDistance = distance
